/**
 * A tiny in-memory, TTL-based async cache.
 *
 * The dashboard (and potentially other read-heavy views) calls the API for
 * aggregate counts. Without caching, every navigation back to the dashboard
 * would hit the backend — and therefore the database — again. `getCached`
 * memoises the resolved value for `ttl` milliseconds and de-duplicates
 * concurrent requests for the same key (so a double-mount in React StrictMode,
 * or several widgets reading the same key, only triggers a single request).
 *
 * The cache is process-local (cleared on full page reload). That is the
 * intended scope: it spares the database from repeated reads while the SPA is
 * open, without trying to be a persistent store.
 */

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

/** Default time-to-live for a cached value: 5 minutes. */
export const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

const store = new Map<string, CacheEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Return the cached value for `key` if it is still fresh, otherwise call
 * `fetcher`, cache its resolved value for `ttl` ms, and return it. Concurrent
 * calls for the same key share a single in-flight promise.
 */
export async function getCached<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TTL
): Promise<T> {
  const now = Date.now();

  const entry = store.get(key) as CacheEntry<T> | undefined;
  if (entry && entry.expiresAt > now) {
    return entry.value;
  }

  const pending = inFlight.get(key) as Promise<T> | undefined;
  if (pending) {
    return pending;
  }

  const promise = fetcher()
    .then((value) => {
      store.set(key, { value, expiresAt: Date.now() + ttl });
      inFlight.delete(key);
      return value;
    })
    .catch((error) => {
      // Do not cache failures — let the next caller retry.
      inFlight.delete(key);
      throw error;
    });

  inFlight.set(key, promise);
  return promise;
}

/**
 * Drop a single cached entry (`key` given) or the entire cache (no argument).
 * Call this after a mutation invalidates a cached aggregate (e.g. creating a
 * post should expire the dashboard counts).
 */
export function invalidateCache(key?: string): void {
  if (key === undefined) {
    store.clear();
    inFlight.clear();
    return;
  }

  store.delete(key);
  inFlight.delete(key);
}

/** Read a cached value without triggering a fetch. Returns `undefined` if absent or stale. */
export function peekCache<T>(key: string): T | undefined {
  const entry = store.get(key) as CacheEntry<T> | undefined;
  if (entry && entry.expiresAt > Date.now()) {
    return entry.value;
  }
  return undefined;
}
